import { useInfiniteQuery } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { useEffect, useMemo, useRef } from "react";
import { fetchData } from "@/utils/api-handler";
import { useProtectedQuery } from "@/hooks/use-protected-query";

export interface MessageContentItem {
    type: 'text' | 'tool_use' | 'thinking' | string;
    text?: string;
    thinking?: string;
    id?: string;
    name?: string;
    input?: any;
}

export interface MessageResponse {
    id: string;
    content: MessageContentItem[];
    role: 'USER' | 'ASSISTANT';
    created_at: string;
    updated_at: string;
    status: string;
    message_type: string;
    is_regenerated: boolean;
    regeneration_count?: number;
    parent_message_id?: string | null;
    reasoning?: string;
    sources?: Array<{ title: string; url: string }>;
    message_metadata?: { attached_files?: Array<{ id: string; file_name: string; file_url?: string; file_size?: number; mime_type?: string }> };
}

export interface MessagesListResponse {
    item_count: number;
    page_count: number;
    items: MessageResponse[];
}

export interface ChatMessage {
    id: string;
    content: string;
    role: 'user' | 'assistant';
    timestamp: Date;
    reasoning?: string;
    sources?: Array<{ title: string; url: string }>;
    isStreaming?: boolean;
    structuredContent?: MessageContentItem[]; // New structured content from API
    attachedFiles?: Array<{ id: string; file_name: string; file_url?: string; file_size?: number; mime_type?: string }>;
    // For branching support
    parent_message_id?: string | null;
    is_regenerated?: boolean;
    status?: string;
}

export function useCommandMessages(agentId: string | null, sessionId: string | null) {
    const { ref: loadMoreRef, inView } = useInView({
        threshold: 0,
        triggerOnce: false,
    });

    const { hasPermission: canListMessages } = useProtectedQuery({
        queryKey: ["_gated_list_command_messages"],
        queryFn: async () => null,
        requiredPermissions: ["LIST_COMMAND_MESSAGES"],
        enabled: false,
    });

    const {
        data: messagesData,
        isLoading: isLoadingMessages,
        isFetching: isFetchingMessages,
        isError: isMessagesError,
        error: messagesError,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useInfiniteQuery<MessagesListResponse>({
        queryKey: ["command-messages", agentId, sessionId],
        initialPageParam: 1,
        queryFn: async ({ pageParam }) => {
            if (!sessionId || !agentId) {
                return { item_count: 0, page_count: 0, items: [] };
            }
            const params = new URLSearchParams({
                page: String(pageParam),
                limit: "30",
                sort_by: "created_at",
                sort_order: "desc", // Newest first, so we can prepend older messages at the top when scrolling up
            });
            const response = await fetchData(
                `/api/command-team/agents/${agentId}/sessions/${sessionId}/messages?${params.toString()}`
            );
            return response;
        },
        getNextPageParam: (lastPage, pages) => {
            if (pages.length < lastPage.page_count) {
                return pages.length + 1;
            }
            return undefined;
        },
        enabled: canListMessages && Boolean(sessionId) && Boolean(agentId),
        maxPages: undefined, // Allow loading all pages as user scrolls
    });

    // Convert API messages to ChatMessage format
    // API returns desc (newest first): page 1 = newest 30, page 2 = next 30 older, etc.
    // For chat UI: we want oldest at top, newest at bottom
    // So we reverse the entire flattened array
    const allMessages = useMemo(() => {
        if (!messagesData?.pages) return [];
        // Flatten all pages (newest messages first from API); dedupe by id in case refetch overlaps pages
        const rawFlat = messagesData.pages.flatMap((page) => page.items);
        const seen = new Set<string>();
        const apiMessages = rawFlat.filter((msg) => {
            if (seen.has(msg.id)) return false;
            seen.add(msg.id);
            return true;
        });
        // Convert to ChatMessage format
        const convertedMessages = apiMessages.map((msg) => {
            const base = {
                id: msg.id,
                role: msg.role.toLowerCase() as 'user' | 'assistant',
                timestamp: new Date(msg.created_at),
                reasoning: msg.reasoning,
                sources: msg.sources,
                isStreaming: false,
                parent_message_id: msg.parent_message_id,
                is_regenerated: msg.is_regenerated,
                status: msg.status,
            };
            const attachedFiles = msg.message_metadata?.attached_files?.length
                ? msg.message_metadata.attached_files
                : undefined;
            // Check if content is array (new format with structured types)
            if (Array.isArray(msg.content)) {
                return {
                    ...base,
                    content: '',
                    structuredContent: msg.content,
                    ...(attachedFiles && { attachedFiles }),
                };
            } else {
                return {
                    ...base,
                    content: (msg.content as any),
                    ...(attachedFiles && { attachedFiles }),
                };
            }
        }) as ChatMessage[];
        // Reverse so oldest messages are at the top, newest at the bottom
        return convertedMessages.reverse();
    }, [messagesData]);

    const initialLoadCompleteRef = useRef(false);
    const fetchCooldownRef = useRef(false);
    const prevSessionIdRef = useRef<string | null>(sessionId);

    // Reset when session changes
    useEffect(() => {
        if (prevSessionIdRef.current !== sessionId) {
            initialLoadCompleteRef.current = false;
            fetchCooldownRef.current = false;
            prevSessionIdRef.current = sessionId;
        }
    }, [sessionId]);

    // Mark initial load as complete after first fetch finishes and a brief delay for scroll to bottom
    useEffect(() => {
        if (
            messagesData?.pages &&
            messagesData.pages.length > 0 &&
            !initialLoadCompleteRef.current &&
            !isLoadingMessages
        ) {
            // Brief delay to let the initial scroll-to-bottom complete before enabling pagination
            const timer = setTimeout(() => {
                initialLoadCompleteRef.current = true;
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [messagesData, isLoadingMessages]);

    // Reset fetch cooldown when sentinel scrolls out of view (after scroll-position adjustment).
    // Uses a delay so rapid in/out flickers during streaming don't reset the cooldown.
    useEffect(() => {
        if (!inView && fetchCooldownRef.current) {
            const timer = setTimeout(() => {
                fetchCooldownRef.current = false;
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [inView]);

    // Load more when scrolling to top (only after initial load is complete).
    // The cooldown ref ensures exactly one fetch per scroll-to-top: after a fetch
    // the sentinel stays briefly in view (observer is async), but the cooldown blocks
    // a second fetch until the sentinel exits the viewport and re-enters.
    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage && initialLoadCompleteRef.current && !fetchCooldownRef.current) {
            fetchCooldownRef.current = true;
            fetchNextPage();
        }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    return {
        allMessages,
        isLoadingMessages,
        isFetchingMessages,
        isFetchingNextPage,
        hasNextPage,
        loadMoreRef,
        canListMessages,
        isMessagesError,
        messagesError,
    };
}

